import React, { useMemo, useState } from 'react';

import { Loading } from '../../components/Loading/Loading';
import { useGetMerchantBalancesQuery, useGetTraderBalancesQuery } from '../../redux/services/AnalystApi';
import { type MerchantBalance, type TraderBalance } from '../../redux/types/AnalystTypes';
import { AnalystPage } from './components/AnalystPage';
import { type Column, SortableTable } from './components/SortableTable';
import { errorText, fmt, toneOf } from './helpers';
import styles from './Analyst.module.css';

const sum = <T,>(rows: T[], get: (row: T) => string) => rows.reduce((acc, row) => acc + Number(get(row) || 0), 0);

const money = <T,>(key: string, name: string, get: (row: T) => string, tone = false): Column<T> => ({
  key,
  name,
  numeric: true,
  value: get,
  render: (row) => <span className={tone ? styles[toneOf(get(row))] : ''}>{fmt(get(row), 3)}</span>,
});

const merchantColumns: Array<Column<MerchantBalance>> = [
  {
    key: 'username',
    name: 'Мерчант',
    value: (r) => r.username,
    render: (r) => (
      <>
        {r.username}
        {r.integration_merchant && <span className={styles.note}> · интеграционный</span>}
        {!r.is_active && <span className={styles.note}> · выкл.</span>}
      </>
    ),
  },
  money('to_merchant', 'Площадка должна мерчанту', (r) => r.available_to_merchant),
  money('to_platform', 'Мерчант должен площадке', (r) => r.available_to_platform),
  money('net', 'Сальдо', (r) => r.net_usdt, true),
  money('pending', 'Сеттлменты в работе', (r) => r.settlements_pending_usdt),
  money('received', 'Зачислено за пополнения', (r) => r.received_usdt),
  money('payout', 'Долг по выплатам', (r) => r.payout_debt_usdt),
  money('m2p', 'Сеттлменты M2P', (r) => r.settlements_m2p_usdt),
  money('p2m', 'Сеттлменты P2M', (r) => r.settlements_p2m_usdt),
  money('adj_d', 'Корр. по пополнениям', (r) => r.adjusted_deposit_usdt),
  money('adj_w', 'Корр. по выплатам', (r) => r.adjusted_withdrawal_usdt),
];

const traderColumns: Array<Column<TraderBalance>> = [
  {
    key: 'username',
    name: 'Трейдер',
    value: (r) => r.username,
    render: (r) => (
      <>
        {r.username}
        {!r.is_active && <span className={styles.note}> · выкл.</span>}
      </>
    ),
  },
  { key: 'currencies', name: 'Валюты', value: (r) => r.currencies.join(', ') },
  money('balance', 'Баланс', (r) => r.balance, true),
  money('frozen', 'Заморожено', (r) => r.frozen_balance),
  money('total', 'Всего', (r) => r.total, true),
  money('overdraft', 'Овердрафт', (r) => r.available_overdraft),
  {
    key: 'source',
    name: 'Источник',
    value: (r) => r.source,
    render: (r) => <span className={styles.note}>{r.source === 'redis' ? 'живой (Redis)' : 'база'}</span>,
  },
];

export const AnalystBalances = () => {
  const [tab, setTab] = useState<'merchants' | 'traders'>('merchants');
  const [hideZero, setHideZero] = useState(true);
  const [search, setSearch] = useState('');
  const merchants = useGetMerchantBalancesQuery(undefined, { skip: tab !== 'merchants' });
  const traders = useGetTraderBalancesQuery(undefined, { skip: tab !== 'traders', pollingInterval: 30000 });

  const merchantRows = useMemo(
    () =>
      (merchants.data || []).filter(
        (r) =>
          r.username.toLowerCase().includes(search.trim().toLowerCase()) &&
          (!hideZero || Number(r.available_to_merchant) || Number(r.available_to_platform) || Number(r.settlements_pending_usdt)),
      ),
    [merchants.data, search, hideZero],
  );
  const traderRows = useMemo(
    () =>
      (traders.data || []).filter(
        (r) => r.username.toLowerCase().includes(search.trim().toLowerCase()) && (!hideZero || Number(r.total)),
      ),
    [traders.data, search, hideZero],
  );
  const current = tab === 'merchants' ? merchants : traders;

  return (
    <AnalystPage
      title='Балансы'
      hint={
        <>
          Мерчанты: «площадка должна» — зачислено за пополнения минус ручные корректировки и сеттлменты M2P (созданные,
          в работе и завершённые); «мерчант должен» — долг по выплатам минус корректировки и сеттлменты P2M. Те же числа
          видит мерчант у себя в кабинете. Трейдеры: живой баланс из Redis, без него — из базы. Все суммы в USDT.
        </>
      }
    >
      <div className={styles.filters}>
        <button type='button' className={tab === 'merchants' ? styles.tabActive : styles.tab} onClick={() => setTab('merchants')}>
          Мерчанты
        </button>
        <button type='button' className={tab === 'traders' ? styles.tabActive : styles.tab} onClick={() => setTab('traders')}>
          Трейдеры
        </button>
        <input type='search' placeholder='Логин' value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className={styles.note} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type='checkbox' checked={hideZero} onChange={(e) => setHideZero(e.target.checked)} style={{ height: 'auto' }} />
          Скрыть нулевые
        </label>
        <button type='button' className={styles.button} onClick={() => current.refetch()}>
          Обновить
        </button>
      </div>
      {current.isError && <div className={styles.error}>{errorText(current.error)}</div>}
      {current.isLoading && <Loading />}
      {tab === 'merchants' && merchants.data && (
        <SortableTable
          rows={merchantRows}
          columns={merchantColumns}
          rowKey={(r) => String(r.id)}
          total={{
            username: `Итого (${merchantRows.length})`,
            to_merchant: fmt(sum(merchantRows, (r) => r.available_to_merchant), 3),
            to_platform: fmt(sum(merchantRows, (r) => r.available_to_platform), 3),
            net: fmt(sum(merchantRows, (r) => r.net_usdt), 3),
            pending: fmt(sum(merchantRows, (r) => r.settlements_pending_usdt), 3),
          }}
        />
      )}
      {tab === 'traders' && traders.data && (
        <SortableTable
          rows={traderRows}
          columns={traderColumns}
          rowKey={(r) => String(r.id)}
          total={{
            username: `Итого (${traderRows.length})`,
            balance: fmt(sum(traderRows, (r) => r.balance), 3),
            frozen: fmt(sum(traderRows, (r) => r.frozen_balance), 3),
            total: fmt(sum(traderRows, (r) => r.total), 3),
          }}
        />
      )}
    </AnalystPage>
  );
};
